import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { orderAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  Package, 
  Truck, 
  CheckCircle,
  Clock,
  MapPin,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { icon: Clock, label: 'Pending', class: 'status-pending' },
  processing: { icon: Package, label: 'Processing', class: 'status-processing' },
  shipped: { icon: Truck, label: 'Shipped', class: 'status-processing' },
  in_transit: { icon: Truck, label: 'In Transit', class: 'status-processing' },
  delivered: { icon: CheckCircle, label: 'Delivered', class: 'status-delivered' },
  completed: { icon: CheckCircle, label: 'Completed', class: 'status-completed' },
};

const paymentStatusConfig = {
  pending: { label: 'Pending', class: 'status-pending' },
  completed: { label: 'Paid', class: 'status-completed' },
  failed: { label: 'Failed', class: 'bg-red-500/10 text-red-400' },
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await orderAPI.getAll();
        setOrders(response.data || []);
      } catch (error) {
        toast.error('Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const downloadInvoice = (orderId) => {
    const token = localStorage.getItem('metag_token');
    const url = `${BACKEND_URL}/api/orders/${orderId}/invoice`;
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `MeTag_Invoice_${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Invoice downloaded!');
    })
    .catch(() => toast.error('Failed to download invoice'));
  };

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Orders & Invoices</h1>
        <p className="text-slate-400">Track your orders and download invoices</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No orders yet</h3>
          <p className="text-slate-400">Your orders will appear here once you make a purchase.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass rounded-2xl p-6"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Order #{order.id?.slice(0, 8)}</p>
                  <h3 className="text-lg font-semibold text-white">{order.productName}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`status-badge ${paymentStatusConfig[order.paymentStatus]?.class || 'status-pending'}`}>
                    {paymentStatusConfig[order.paymentStatus]?.label || order.paymentStatus}
                  </span>
                  <span className={`status-badge ${statusConfig[order.shippingStatus]?.class || 'status-pending'}`}>
                    {statusConfig[order.shippingStatus]?.label || order.shippingStatus}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 mb-1">Order Date</p>
                  <p className="text-white">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Amount</p>
                  <p className="text-white">₹{order.discountPrice}</p>
                </div>
                <div>
                  <p className="text-slate-500 mb-1">Shipping Address</p>
                  <div className="flex items-start gap-1 text-white">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                    <span className="truncate">
                      {order.address?.city}, {order.address?.state}
                    </span>
                  </div>
                </div>
              </div>

              {order.trackingNumber && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-slate-500 text-sm mb-1">Tracking Number</p>
                  <p className="text-white font-mono">{order.trackingNumber}</p>
                  {order.carrier && (
                    <p className="text-slate-400 text-sm mt-1">Carrier: {order.carrier}</p>
                  )}
                </div>
              )}

              {/* Invoice Download Button */}
              {order.paymentStatus === 'completed' && (
                <div className="mt-4 pt-4 border-t border-white/5 flex justify-end">
                  <Button
                    onClick={() => downloadInvoice(order.id)}
                    variant="outline"
                    className="btn-secondary text-sm flex items-center gap-2"
                    data-testid={`download-invoice-${order.id}`}
                  >
                    <Download className="w-4 h-4" />
                    Download Invoice
                  </Button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
